<!-- Shared popup menu for row secondary actions (resend / reprocess / release) -->
<p-toast />
<p-menu
  #rowActionMenu
  [model]="actionMenuItems"
  [popup]="true"
  appendTo="body"
  (onHide)="activeMenuRow = null"
></p-menu>

<div>
  <app-page-breadcrumb
    pageTitle="Document Tracker"
    [breadcrumbs]="breadcrumbs"
  />
  <div
    class="min-h-screen rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]"
  >
    <div class="w-full max-w-screen-2xl mx-auto">
      <div
        class="flex flex-col px-6 py-4 gap-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <h2 class="text-xl font-semibold text-gray-800 dark:text-white/90">
          Document Tracker
        </h2>
        <form></form>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <!-- Search Bar (always expanded) -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div class="search-wrapper expanded">
              <div class="search-input-wrapper">
                <input
                  #searchInput
                  pInputText
                  type="text"
                  class="search-input"
                  placeholder="Search..."
                  [(ngModel)]="searchValue"
                  (input)="onInputChange($event)"
                  (keydown.enter)="onFilterSearch()"
                  (keydown.escape)="clearSearchInput()"
                  autocomplete="off"
                />

                @if (searchValue) {
                  <button
                    type="button"
                    aria-label="Clear search"
                    (click)="clearSearchInput(searchInput)"
                  >
                    <i class="fa-solid fa-xmark text-sm text-gray-500"></i>
                  </button>
                }

                <button
                  type="button"
                  aria-label="Submit search"
                  (click)="onFilterSearch()"
                >
                  <i
                    class="fa-solid fa-magnifying-glass text-sm text-gray-500"
                  ></i>
                </button>
              </div>
            </div>
          </div>
          <!-- Refresh Button -->
          <div class="relative group">
            <button
              pTooltip="Refresh"
              tooltipPosition="top"
              (click)="refreshTable()"
              class="flex items-center justify-center w-12 h-9 rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 text-gray-600 dark:text-gray-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
          <!-- Auto-Refresh Button -->
          <div class="relative">
            <button
              [pTooltip]="
                autoRefreshInterval > 0
                  ? 'Auto-refresh every ' +
                    autoRefreshInterval +
                    's — next in ' +
                    refreshInSeconds +
                    's'
                  : 'Auto-refresh (off)'
              "
              tooltipPosition="top"
              (click)="showAutoRefreshModal = true"
              [ngClass]="
                autoRefreshInterval > 0
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-600 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-300'
                  : 'border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
              "
              class="flex items-center justify-center w-12 h-9 rounded-lg border transition-colors"
            >
              <i class="pi pi-clock text-base"></i>
            </button>
            @if (autoRefreshInterval > 0) {
              <span
                class="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center shadow"
                >{{ refreshInSeconds ?? autoRefreshInterval }}s</span
              >
            }
          </div>
          <!-- Upload Button -->
          <div class="relative group">
            <button
              type="button"
              pTooltip="Upload"
              tooltipPosition="top"
              (click)="openUploadModal()"
              [disabled]="!privileges.includes('UPLOAD_FILE')"
              class="flex items-center justify-center w-12 h-9 rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-6 h-6 text-gray-500 dark:text-gray-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="12" y1="3" x2="12" y2="15" />
                <line x1="12" y1="3" x2="7" y2="8" />
                <line x1="12" y1="3" x2="17" y2="8" />
                <path d="M5 14 L5 19 Q5 21 7 21 L17 21 Q19 21 19 19 L19 14" />
              </svg>
            </button>
          </div>
          <!-- Export Button -->
          <div class="relative group">
            <button
              [disabled]="!privileges.includes('VIEW_DOC_TRACKER_LIST')"
              pTooltip="Export"
              tooltipPosition="top"
              (click)="onClickExport()"
              class="flex items-center justify-center w-12 h-9 rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-6 h-6 text-gray-600 dark:text-gray-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 3v12m0 0l-4-4m4 4l4-4"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                />
              </svg>
            </button>
          </div>
          <!-- Column Settings -->
          <div class="relative group">
            <button
              pTooltip="View Column Settings"
              tooltipPosition="top"
              (click)="openColumnSettingsModal()"
              class="w-13 h-9 flex items-center justify-center rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-6 h-6 text-gray-700 dark:text-gray-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <circle
                  cx="4"
                  cy="7"
                  r="1.2"
                  fill="currentColor"
                  stroke="none"
                />
                <line x1="7.5" y1="7" x2="20" y2="7" />
                <circle
                  cx="4"
                  cy="12"
                  r="1.2"
                  fill="currentColor"
                  stroke="none"
                />
                <line x1="7.5" y1="12" x2="20" y2="12" />
                <circle
                  cx="4"
                  cy="17"
                  r="1.2"
                  fill="currentColor"
                  stroke="none"
                />
                <line x1="7.5" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          </div>

          <!-- Filter  -->
          <button
            pTooltip="Filter"
            tooltipPosition="top"
            (click)="onFilterClick()"
            class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
          >
            <svg
              class="stroke-current fill-white dark:fill-gray-800"
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.29004 5.90393H17.7067"
                stroke=""
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M17.7075 14.0961H2.29085"
                stroke=""
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M12.0826 3.33331C13.5024 3.33331 14.6534 4.48431 14.6534 5.90414C14.6534 7.32398 13.5024 8.47498 12.0826 8.47498C10.6627 8.47498 9.51172 7.32398 9.51172 5.90415C9.51172 4.48432 10.6627 3.33331 12.0826 3.33331Z"
                fill=""
                stroke=""
                stroke-width="1.5"
              ></path>
              <path
                d="M7.91745 11.525C6.49762 11.525 5.34662 12.676 5.34662 14.0959C5.34661 15.5157 6.49762 16.6667 7.91745 16.6667C9.33728 16.6667 10.4883 15.5157 10.4883 14.0959C10.4883 12.676 9.33728 11.525 7.91745 11.525Z"
                fill=""
                stroke=""
                stroke-width="1.5"
              ></path>
            </svg>
            Filter
          </button>
        </div>
      </div>

      <div class="overflow-hidden">
        <div class="relative mx-5 sm:mx-6">
          @if (isTableLoading) {
            <div
              class="absolute inset-0 z-[60] flex flex-col items-center justify-center bg-white/70 dark:bg-gray-900/70 backdrop-blur-[2px] rounded-lg"
            >
              <svg
                class="animate-spin w-8 h-8 text-indigo-500 mb-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                />
              </svg>
              <span class="text-sm text-indigo-500 font-medium"
                >Refreshing...</span
              >
            </div>
          }
          <div
            class="max-w-full overflow-x-auto overflow-y-auto relative max-h-[75vh]"
            #tableContainer
          >
            <table class="min-w-full border-collapse table-fixed">
              <!-- HEADER -->
              <thead
                class="sticky top-0 z-50 bg-white dark:bg-gray-900 border-gray-100 dark:border-white/[0.05]"
              >
                <tr>
                  <!-- ============================== -->
                  <!-- NEW: CHECKBOX / MULTISELECT COLUMN -->
                  <!-- ============================== -->
                  <th
                    class="px-3 py-3 text-left text-sm border-b select-none sticky left-0 top-0 z-40 bg-white dark:bg-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    [style.min-width]="'35px'"
                    [style.width]="'35px'"
                  >
                    <p-checkbox
                      styleClass="small-checkbox"
                      [binary]="true"
                      [(ngModel)]="selectAll"
                      (onChange)="toggleSelectAll($event)"
                    ></p-checkbox>
                  </th>

                  <!-- ============================== -->
                  <!-- NEW: ACTION COLUMN HEADER -->
                  <!-- ============================== -->
                  <th
                    class="px-4 py-3 font-medium text-gray-600 text-left text-sm whitespace-nowrap dark:text-gray-400 border-b select-none sticky left-[40px] top-0 z-40 bg-white dark:bg-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    [style.min-width]="'120px'"
                    [style.width]="'120px'"
                  >
                    Actions
                  </th>

                  <!-- EXISTING DYNAMIC COLUMNS (offsets shifted to account for the 2 new sticky columns) -->
                  <th
                    *ngFor="let col of column_list; let i = index"
                    (click)="col.is_sortable && sortColumn(col.key)"
                    class="px-4 py-3 font-medium text-gray-600 text-left text-sm whitespace-nowrap dark:text-gray-400 border-b select-none overflow-hidden"
                    [ngClass]="[
                      i === 0
                        ? 'sticky left-[150px] top-0 z-40 bg-white dark:bg-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                        : '',
                      i === 1
                        ? 'sticky left-[290px] top-0 z-40 bg-white dark:bg-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                        : '',
                    ]"
                    [style.min-width]="
                      i === 0
                        ? '140px'
                        : i === 1
                          ? '150px'
                          : col.width
                            ? col.width + 'px'
                            : 'auto'
                    "
                    [style.width.px]="i > 1 ? col.width : null"
                    [style.max-width.px]="i > 1 ? col.width : null"
                  >
                    <div class="flex items-center justify-start gap-1">
                      {{ col.label }}
                      <!-- Prime Sort Icon -->
                      <ng-container *ngIf="col.is_sortable">
                        <i
                          class="pi text-xs"
                          [ngClass]="{
                            'pi-sort-alt text-gray-400 dark:text-gray-500':
                              order_by !== col.key,
                            'pi-sort-amount-up-alt text-brand-500':
                              order_by === col.key && order_type === 0,
                            'pi-sort-amount-down text-brand-500':
                              order_by === col.key && order_type === 1,
                          }"
                        ></i>
                      </ng-container>
                    </div>
                  </th>
                </tr>
              </thead>

              <!-- BODY -->
              <tbody class="divide-y divide-gray-100 dark:divide-white/[0.05]">
                <tr
                  *ngFor="let row of currentItems; let rowIndex = index"
                  [ngClass]="{
                    'bg-indigo-100 dark:bg-gray-700': activeMenuRow === row,
                  }"
                >
                  <!-- ============================== -->
                  <!-- NEW: CHECKBOX / MULTISELECT CELL -->
                  <!-- ============================== -->
                  <td
                    class="px-3 py-3 text-left text-sm sticky left-0 top-0 z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    [ngClass]="
                      activeMenuRow === row
                        ? 'bg-indigo-100 dark:bg-gray-700'
                        : 'bg-white dark:bg-gray-900'
                    "
                    [style.width]="'35px'"
                  >
                    <p-checkbox
                      styleClass="small-checkbox"
                      [binary]="true"
                      [(ngModel)]="row.selected"
                      (onChange)="onRowSelect(row)"
                    ></p-checkbox>
                  </td>
                  <!-- ============================== -->
                  <!-- NEW: ACTION COLUMN CELL -->
                  <!-- ============================== -->
                  <td
                    class="px-4 py-3 text-left text-sm sticky left-[40px] top-0 z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                    [ngClass]="
                      activeMenuRow === row
                        ? 'bg-indigo-100 dark:bg-gray-700'
                        : 'bg-white dark:bg-gray-900'
                    "
                    [style.width]="'120px'"
                  >
                    <div class="flex items-center gap-2">
                      <!-- PRIMARY: Send Mail (only when `send` or `mail` is configured) -->
                      <button
                        *ngIf="canSendMail(row)"
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10 px-2.5 py-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 transition"
                        [ngClass]="
                          selectedCount >= 2
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-indigo-100 dark:hover:bg-indigo-500/20 cursor-pointer'
                        "
                        [disabled]="selectedCount >= 2"
                        pTooltip="Send Mail"
                        tooltipPosition="top"
                        (click)="selectedCount < 2 && onSendEmail(row)"
                      >
                        <i class="pi pi-send text-sm"></i>
                        <!-- Send Mail -->
                      </button>

                      <!-- SECONDARY: three-dot menu (always visible, disabled when no actions) -->
                      <button
                        type="button"
                        class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 transition"
                        [ngClass]="{
                          'opacity-50 cursor-not-allowed':
                            selectedCount >= 2 || !hasSecondaryActions(row),
                          'hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer':
                            selectedCount < 2 && hasSecondaryActions(row),
                        }"
                        [disabled]="
                          selectedCount >= 2 || !hasSecondaryActions(row)
                        "
                        pTooltip="More actions"
                        tooltipPosition="top"
                        (click)="
                          selectedCount < 2 &&
                            hasSecondaryActions(row) &&
                            openActionMenu(row, $event)
                        "
                      >
                        <i class="pi pi-ellipsis-v text-base"></i>
                      </button>
                    </div>
                  </td>

                  <!-- EXISTING DYNAMIC COLUMNS -->
                  <td
                    *ngFor="let col of column_list; let i = index"
                    class="px-4 py-3 text-left text-gray-700 text-sm whitespace-nowrap dark:text-gray-300 min-w-[100px] max-w-[164px] truncate overflow-hidden"
                    [pTooltip]="getCellTooltip(row, col)"
                    tooltipPosition="top"
                    [ngClass]="[
                      i === 0
                        ? 'sticky left-[150px] top-0 z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ' +
                          (activeMenuRow === row
                            ? 'bg-indigo-100 dark:bg-gray-700'
                            : 'bg-white dark:bg-gray-900')
                        : '',
                      i === 1
                        ? 'sticky left-[290px] top-0 z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ' +
                          (activeMenuRow === row
                            ? 'bg-indigo-100 dark:bg-gray-700'
                            : 'bg-white dark:bg-gray-900')
                        : '',
                      col.key !== 'created_on'
                        ? 'min-w-[100px] max-w-[164px] truncate overflow-hidden'
                        : 'min-w-[180px]',
                    ]"
                    [style.width]="
                      i === 0
                        ? '140px'
                        : i === 1
                          ? '150px'
                          : col.width
                            ? col.width + 'px'
                            : '164px'
                    "
                    [style.min-width.px]="i > 1 ? col.width : null"
                    [style.max-width.px]="i > 1 ? col.width : null"
                  >
                    <!-- Date -->
                    <ng-container
                      *ngIf="
                        col.key === 'created_on' ||
                          col.key === 'modified_on' ||
                          col.key === 'document_date';
                        else checkBoolean
                      "
                    >
                      <span
                        [innerHTML]="
                          row[col.key]
                            ? (row[col.key] | appDate | highlight: serchInput)
                            : ''
                        "
                      >
                      </span>
                    </ng-container>

                    <!-- Boolean -->
                    <ng-template #checkBoolean>
                      <!-- STATUS COLUMN -->
                      <ng-container
                        *ngIf="col.key === 'status'; else checkIsValid"
                      >
                        <div class="flex justify-start">
                          <span
                            class="px-2 text-[12px] font-semibold rounded-full inline-flex items-center"
                            [style.backgroundColor]="
                              getStatusColor(row[col.key]) + '20'
                            "
                            [style.color]="getStatusColor(row[col.key])"
                            [style.border]="
                              '1px solid ' + getStatusColor(row[col.key]) + '30'
                            "
                            [class.status-blink]="
                              isStatusHighlight(row[col.key])
                            "
                          >
                            <span
                              class="relative z-10"
                              [innerHTML]="row[col.key] | highlight: serchInput"
                            >
                            </span>
                          </span>
                        </div>
                      </ng-container>
                    </ng-template>

                    <!-- IS_VALID BOOLEAN -->
                    <ng-template #checkIsValid>
                      <ng-container
                        *ngIf="col.key === 'is_valid'; else checkFile"
                      >
                        <span
                          class="px-1.5 py-0.5 text-[10px] font-semibold rounded-full"
                          [ngClass]="
                            row[col.key]
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                          "
                        >
                          {{ row[col.key] ? "YES" : "NO" }}
                        </span>
                      </ng-container>
                    </ng-template>

                    <!-- File Icon -->
                    <ng-template #checkFile>
                      <ng-container
                        *ngIf="col.key === 'ack_source_url'; else checkDocFiles"
                      >
                        <div class="flex justify-center items-center">
                          <!-- Show icon if data exists -->
                          <button
                            *ngIf="row[col.key]?.data"
                            type="button"
                            class="transition duration-200"
                            [ngClass]="
                              row['acknowledgement_status'] === 'ACCEPTED' ||
                              row['acknowledgement_status'] === 'SUCCESS'
                                ? 'text-green-500 hover:text-green-600'
                                : 'text-red-500 hover:text-red-600'
                            "
                            [pTooltip]="
                              row['acknowledgement_status'] === 'ACCEPTED' ||
                              row['acknowledgement_status'] === 'SUCCESS'
                                ? 'Accepted Acknowledgement'
                                : 'Rejected Acknowledgement'
                            "
                            tooltipPosition="top"
                            (click)="
                              handleAcknowlegementClick(
                                col.key,
                                row[col.key]?.data,
                                row
                              )
                            "
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              class="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M10 3v4a1 1 0 0 1-1 1H5m4 10v-2m3 2v-6m3 6v-3m4-11v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1Z"
                              />
                            </svg>
                          </button>

                          <!-- No Data -->
                          <span
                            *ngIf="!row[col.key]?.data"
                            class="text-gray-500 text-sm dark:text-gray-400"
                            [innerHTML]="'N/A' | highlight: serchInput"
                          >
                          </span>
                        </div>
                      </ng-container>

                      <!-- NORMAL FILES -->
                      <ng-template #checkDocFiles>
                        <ng-container
                          *ngIf="
                            col.key === 'document_source_url' ||
                              col.key === 'out_doc_source_url';
                            else normalValue
                          "
                        >
                          <div class="flex justify-center items-center">
                            <button
                              *ngIf="row[col.key]"
                              type="button"
                              class="text-blue-500 hover:text-blue-600 transition duration-200"
                              [pTooltip]="
                                col.key === 'document_source_url'
                                  ? 'Input File'
                                  : col.key === 'out_doc_source_url'
                                    ? 'Output File'
                                    : ''
                              "
                              tooltipPosition="top"
                              (click)="
                                handleFileClick(
                                  row,
                                  col.key,
                                  col.key === 'out_doc_source_url'
                                    ? row[col.key]?.data
                                    : row[col.key]
                                )
                              "
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M10 3v4a1 1 0 0 1-1 1H5m4 10v-2m3 2v-6m3 6v-3m4-11v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1Z"
                                />
                              </svg>
                            </button>
                          </div>
                        </ng-container>
                      </ng-template>
                    </ng-template>

                    <!-- Normal Value -->
                    <ng-template #normalValue>
                      <!-- WFID Hyperlink -->
                      <ng-container
                        *ngIf="col.key === 'wfid'; else checkMapName"
                      >
                        <span
                          class="text-blue-500 hover:text-blue-600 underline cursor-pointer font-medium"
                          (click)="infoClick(row)"
                          [innerHTML]="row[col.key] | highlight: serchInput"
                        >
                        </span>
                      </ng-container>

                      <!-- MAP NAME Hyperlink -->
                      <ng-template #checkMapName>
                        <ng-container
                          *ngIf="
                            col.key === 'map_name';
                            else checkTpProfileName
                          "
                        >
                          <div class="flex justify-start">
                            <span
                              class="block max-w-[164px] truncate text-blue-500 hover:text-blue-600 underline cursor-pointer font-medium"
                              (click)="openMap(row)"
                              [innerHTML]="row[col.key] | highlight: serchInput"
                            >
                            </span>
                          </div>
                        </ng-container>
                      </ng-template>

                      <!-- TP Profile Name -->
                      <ng-template #checkTpProfileName>
                        <ng-container
                          *ngIf="
                            col.key === 'tp_profile_contract';
                            else checkSourceProfile
                          "
                        >
                          <div class="flex justify-start">
                            <span
                              class="block max-w-[164px] truncate text-blue-500 hover:text-blue-600 underline cursor-pointer font-medium"
                              (click)="openConnectivity(row)"
                              [innerHTML]="row[col.key] | highlight: serchInput"
                            >
                            </span>
                          </div>
                        </ng-container>
                      </ng-template>

                      <!-- Source Profile -->
                      <ng-template #checkSourceProfile>
                        <ng-container
                          *ngIf="
                            col.key === 'source_connectivity_profile' &&
                              row[col.key] !== 'Manual' &&
                              row[col.key] !== 'Uploaded from UI';
                            else checkDestinationProfile
                          "
                        >
                          <div class="flex justify-start">
                            <span
                              class="block text-blue-500 hover:text-blue-600 underline cursor-pointer font-medium text-left"
                              (click)="openSourceProfile(row)"
                              [innerHTML]="row[col.key] | highlight: serchInput"
                            >
                            </span>
                          </div>
                        </ng-container>
                      </ng-template>

                      <!-- Destination Profile -->
                      <ng-template #checkDestinationProfile>
                        <ng-container
                          *ngIf="
                            col.key === 'destination_connectivity_profile';
                            else defaultText
                          "
                        >
                          <div class="flex justify-start">
                            <span
                              class="block max-w-[164px] truncate text-blue-500 hover:text-blue-600 underline cursor-pointer font-medium"
                              (click)="openDestinationProfile(row)"
                              [innerHTML]="row[col.key] | highlight: serchInput"
                            >
                            </span>
                          </div>
                        </ng-container>
                      </ng-template>

                      <!-- Default Text -->
                      <ng-template #defaultText>
                        <ng-container
                          *ngIf="
                            col.key === 'acknowledgement_received_on';
                            else otherFields
                          "
                        >
                          <span
                            [ngClass]="
                              !row[col.key]
                                ? 'text-gray-500 dark:text-gray-400'
                                : row['sla_exceeded'] === 'Within SLA'
                                  ? 'text-green-500'
                                  : 'text-red-500'
                            "
                            [pTooltip]="row['sla_exceeded'] || 'N/A'"
                            tooltipPosition="top"
                            class="cursor-pointer"
                            [innerHTML]="
                              row[col.key]
                                ? (row[col.key] | appDate)
                                : ('N/A' | highlight: serchInput)
                            "
                          >
                          </span>
                        </ng-container>

                        <ng-template #otherFields>
                          <ng-container
                            *ngIf="
                              col.key === 'sla_exceeded';
                              else normalDefault
                            "
                          >
                            <span
                              [innerHTML]="row[col.key] | highlight: serchInput"
                            ></span>
                          </ng-container>

                          <ng-template #normalDefault>
                            <ng-container
                              *ngIf="
                                col.key === 'reference_key1' ||
                                  col.key === 'reference_key2' ||
                                  col.key === 'reference_key3' ||
                                  col.key === 'reference_value1' ||
                                  col.key === 'reference_value2' ||
                                  col.key === 'reference_value3' ||
                                  col.key === 'direction' ||
                                  col.key === 'in_standard_file_profile' ||
                                  col.key === 'in_file_type' ||
                                  col.key === 'out_file_type' ||
                                  col.key === 'out_standard_file_profile' ||
                                  col.key === 'source_connectivity_profile' ||
                                  col.key === 'sender_id' ||
                                  col.key === 'receiver_id';
                                else trueDefault
                              "
                            >
                              <span
                                class="block max-w-[164px] truncate text-left"
                                [innerHTML]="
                                  row[col.key] | highlight: serchInput
                                "
                              ></span>
                            </ng-container>
                          </ng-template>

                          <ng-template #trueDefault>
                            <span
                              [innerHTML]="row[col.key] | highlight: serchInput"
                            ></span>
                          </ng-template>
                        </ng-template>
                      </ng-template>
                    </ng-template>
                  </td>
                </tr>

              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty State: rendered outside the horizontal scroll container so the
             message stays visible however wide the column set grows. -->
        @if (!loading && !isTableLoading && !currentItems?.length) {
          <div
            class="flex flex-col items-center justify-center px-6 py-14 text-center"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-white/[0.06]"
            >
              <svg
                class="h-6 w-6 text-gray-400 dark:text-gray-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                />
              </svg>
            </div>
            <p
              class="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              No records found
            </p>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              There are no documents matching your current filters. Try
              adjusting the filters or search, or upload a file to add documents.
            </p>
          </div>
        }

        <!-- Pagination Controls -->
        <div
          class="px-4 py-3 border-t border-gray-200 dark:border-white/[0.05]"
        >
          <div class="flex items-center justify-between flex-wrap gap-4">
            <!-- Previous Button -->
            <!-- <app-button
              size="sm"
              variant="outline"
              (btnClick)="goToPage(currentPage - 1)"
              [disabled]="currentPage === 1"
            >
              <svg
                class="fill-current"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M2.58301 9.99868C2.58272 10.1909 2.65588 10.3833 2.80249 10.53L7.79915 15.5301C8.09194 15.8231 8.56682 15.8233 8.85981 15.5305C9.15281 15.2377 9.15297 14.7629 8.86018 14.4699L5.14009 10.7472L16.6675 10.7472C17.0817 10.7472 17.4175 10.4114 17.4175 9.99715C17.4175 9.58294 17.0817 9.24715 16.6675 9.24715L5.14554 9.24715L8.86017 5.53016C9.15297 5.23717 9.15282 4.7623 8.85983 4.4695C8.56684 4.1767 8.09197 4.17685 7.79917 4.46984L2.84167 9.43049C2.68321 9.568 2.58301 9.77087 2.58301 9.99715C2.58301 9.99766 2.58301 9.99817 2.58301 9.99868Z"
                  fill=""
                ></path>
              </svg>
              <span class="hidden sm:inline">Previous</span>
            </app-button> -->
            <div
              class="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap"
            >
              Showing

              {{ getStartRecord() }}

              -

              {{ getEndRecord() }}

              of

              {{ totalCount }} Records
            </div>
            <!-- Page Info -->
            <span
              class="block text-sm font-medium text-gray-700 dark:text-gray-400 sm:hidden"
            >
              Page {{ currentPage }} of {{ totalPages }}
            </span>

            <!-- Page Numbers -->
            <ul class="hidden items-center gap-0.5 sm:flex">
              <li>
                <button
                  (click)="goToPage(currentPage - 1)"
                  [disabled]="currentPage === 1"
                  aria-label="Previous page"
                  class="flex h-10 w-10 items-center justify-center rounded-lg text-theme-sm font-medium text-gray-700 hover:bg-brand-500/[0.08] hover:text-brand-500 dark:text-gray-400 dark:hover:bg-brand-500 dark:hover:text-white disabled:opacity-40 disabled:pointer-events-none"
                >
                  <i class="pi pi-angle-left"></i>
                </button>
              </li>
              @for (page of getVisiblePages(); track page) {
                <li>
                  <button
                    (click)="goToPage(page)"
                    class="flex h-10 w-10 items-center justify-center rounded-lg text-theme-sm font-medium"
                    [ngClass]="{
                      'bg-brand-500 text-white': currentPage === page,
                      'text-gray-700 hover:bg-brand-500/[0.08] dark:hover:bg-brand-500 dark:hover:text-white hover:text-brand-500 dark:text-gray-400':
                        currentPage !== page,
                    }"
                  >
                    {{ page }}
                  </button>
                </li>
              }
              <li>
                <button
                  (click)="goToPage(currentPage + 1)"
                  [disabled]="currentPage === totalPages || totalPages === 0"
                  aria-label="Next page"
                  class="flex h-10 w-10 items-center justify-center rounded-lg text-theme-sm font-medium text-gray-700 hover:bg-brand-500/[0.08] hover:text-brand-500 dark:text-gray-400 dark:hover:bg-brand-500 dark:hover:text-white disabled:opacity-40 disabled:pointer-events-none"
                >
                  <i class="pi pi-angle-right"></i>
                </button>
              </li>
            </ul>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-600 dark:text-gray-400">
                Rows:
              </span>
              <p-select
                [options]="pageSizeOptions"
                [(ngModel)]="itemsPerPage"
                optionLabel="label"
                optionValue="value"
                placeholder="Rows"
                (onChange)="onPageSizeChange()"
                class="w-24"
                [appendTo]="'body'"
              >
              </p-select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
<!--Show the Info Data-->
@if (showLogModal) {
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-999 flex items-center justify-center backdrop-blur-md bg-black/40 dark:bg-black/60"
  >
    <!-- Modal Container -->
    <div
      class="w-[95vw] max-w-7xl max-h-[95vh] flex flex-col rounded-2xl shadow-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
    >
      <!-- Header -->
      <!-- Header -->
      <div
        class="relative z-50 rounded-t-2xl flex justify-between items-center gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50/80 via-white to-transparent dark:from-indigo-950/40 dark:via-gray-900 dark:to-transparent"
      >
        <div class="flex items-center gap-3 flex-wrap">
          <div
            class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-blue-500 to-violet-500 flex items-center justify-center shadow-lg shadow-indigo-500/30"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M14 3v4a1 1 0 0 0 1 1h4"
              />
            </svg>
          </div>
          <div class="flex items-center gap-2.5 flex-wrap">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">
              Document Processing Logs
            </h2>
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 ring-1 ring-inset ring-indigo-200 dark:ring-indigo-500/30"
            >
              WORKFLOW ID : {{ selectedWFID || "N/A" }}
            </span>
            @if (selectedParentWFID) {
              <button
                type="button"
                (click)="openParentWorkflow()"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                pTooltip="Open the uploaded document this workflow came from"
                tooltipPosition="bottom"
              >
                <i class="pi pi-arrow-up-right text-[9px]"></i>
                SOURCE : {{ selectedParentWFID }}
              </button>
            }
          </div>
        </div>

        <!-- Right side: attempt picker + close -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <!-- Attempt dropdown -->
          @if (attempts.length) {
            <div class="relative">
              <button
                type="button"
                (click)="toggleAttemptDropdown()"
                class="flex items-center gap-3 rounded-xl border px-3 py-2 min-w-[15rem] text-left transition-all duration-200 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-500/60"
                [class]="
                  isAttemptDropdownOpen
                    ? 'border-indigo-400 dark:border-indigo-500 ring-1 ring-indigo-500/20 shadow-sm shadow-indigo-500/10'
                    : 'border-gray-200 dark:border-gray-700'
                "
              >
                <span
                  class="flex-1 min-w-0 flex items-center gap-1.5 whitespace-nowrap"
                >
                  @if (activeAttemptInfo; as sel) {
                    <span
                      class="text-sm font-semibold text-gray-900 dark:text-white"
                    >
                      Attempt {{ sel.attempt }}
                    </span>
                    @if (sel.triggered_by) {
                      <span class="text-gray-300 dark:text-gray-600">·</span>
                      <span
                        class="text-sm text-gray-600 dark:text-gray-300 truncate"
                      >
                        {{ sel.triggered_by }}
                      </span>
                    }
                    @if (sel.started_on) {
                      <span class="text-gray-300 dark:text-gray-600">·</span>
                      <span class="text-xs text-gray-400 dark:text-gray-500">
                        {{ sel.started_on | appDate }}
                      </span>
                    }
                  } @else {
                    <span class="text-sm text-gray-500 dark:text-gray-400"
                      >Select an attempt</span
                    >
                  }
                </span>

                <i
                  class="pi pi-chevron-down text-[10px] text-gray-400 dark:text-gray-500 transition-transform duration-200"
                  [class.rotate-180]="isAttemptDropdownOpen"
                ></i>
              </button>

              @if (isAttemptDropdownOpen) {
                <!-- click-away catcher -->
                <div
                  class="fixed inset-0 z-[55]"
                  (click)="closeAttemptDropdown()"
                ></div>

                <div
                  class="absolute right-0 mt-2 w-[24rem] max-h-80 overflow-y-auto z-[60] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl shadow-black/10 p-1.5"
                >
                  @for (a of attempts; track a.attempt) {
                    <button
                      type="button"
                      (click)="selectAttempt(a.attempt)"
                      class="group relative w-full text-left rounded-lg px-3 py-2.5 transition-colors"
                      [class]="
                        selectedAttempt === a.attempt
                          ? 'bg-indigo-50 dark:bg-indigo-950/40'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                      "
                    >
                      @if (selectedAttempt === a.attempt) {
                        <span
                          class="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 via-blue-500 to-violet-500"
                        ></span>
                      }

                      <!-- line 1: name + badges + count -->
                      <span class="flex items-center gap-2 flex-wrap">
                        <span
                          class="text-xs font-semibold"
                          [class]="
                            selectedAttempt === a.attempt
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-200'
                          "
                        >
                          Attempt {{ a.attempt }}
                        </span>

                        @if (a.status) {
                          <span
                            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide ring-1 ring-inset whitespace-nowrap"
                            [ngClass]="statusBadgeClass(a.status)"
                          >
                            <span
                              class="w-1 h-1 rounded-full bg-current opacity-70"
                            ></span>
                            {{ a.status }}
                          </span>
                        }

                        @if (a.trigger) {
                          <span
                            class="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide ring-1 ring-inset whitespace-nowrap"
                            [ngClass]="triggerBadgeClass(a.trigger)"
                          >
                            {{ a.trigger }}
                          </span>
                        }

                        <span
                          class="ml-auto flex-shrink-0 text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 whitespace-nowrap"
                        >
                          {{ attemptLogCount(a) }} logs
                        </span>
                      </span>

                      <!-- line 2: who + when -->
                      <span
                        class="mt-1 flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 min-w-0"
                      >
                        @if (a.triggered_by) {
                          <span class="truncate">{{ a.triggered_by }}</span>
                        }
                        @if (a.triggered_by && a.started_on) {
                          <span class="text-gray-300 dark:text-gray-600"
                            >·</span
                          >
                        }
                        @if (a.started_on) {
                          <span class="truncate">{{
                            a.started_on | appDate
                          }}</span>
                        }
                      </span>
                    </button>
                  }
                </div>
              }
            </div>
          }

          <button
            type="button"
            class="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-40 disabled:pointer-events-none"
            (click)="refreshLogs()"
            [disabled]="isLogsLoading || selectedDocId == null"
            pTooltip="Refresh logs"
            tooltipPosition="left"
          >
            <i class="pi pi-refresh text-sm" [class.animate-spin]="isLogsLoading"></i>
          </button>
          <button
            class="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
            (click)="closeModal()"
            pTooltip="Close"
            tooltipPosition="left"
          >
            <i class="pi pi-times text-sm"></i>
          </button>
        </div>
      </div>

      <!-- Table View -->
      <div
        class="flex-1 overflow-y-auto timeline-scroll relative rounded-b-2xl"
      >
        @if (isLogsLoading) {
          <div
            class="absolute inset-0 z-40 flex flex-col items-center justify-center bg-white/70 dark:bg-gray-900/70 backdrop-blur-[2px]"
          >
            <svg
              class="animate-spin w-8 h-8 text-indigo-500 mb-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
              />
            </svg>
            <span class="text-sm text-indigo-500 font-medium"
              >Loading processing logs...</span
            >
          </div>
        }

        <table class="logs-table min-w-full text-sm">
          <thead
            class="sticky top-0 z-30 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur"
          >
            <tr class="border-b border-gray-200 dark:border-gray-700">
              <th
                class="px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap min-w-[110px]"
              >
                Process ID
              </th>
              <th
                class="px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                Process Name
              </th>
              <th
                class="px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                Status
              </th>
              <th
                class="px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                Payload
              </th>
              <th
                class="px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                Remarks
              </th>
              <th
                class="px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
              >
                Created On
              </th>
            </tr>
          </thead>

          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            @for (log of activeLogs; track log.id) {
              <tr
                class="hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    [style.background-color]="getTimelineColor(log.status)"
                    class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold text-white shadow-sm"
                  >
                    {{ log.code || "—" }}
                  </span>
                </td>

                <td class="px-6 py-4 font-medium text-gray-900 dark:text-white">
                  {{ log.process_name }}
                </td>

                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-2">
                    <span
                      class="w-2 h-2 rounded-full flex-shrink-0"
                      [style.background-color]="getTimelineColor(log.status)"
                    ></span>
                    <span class="text-gray-600 dark:text-gray-300">{{
                      log.status || "N/A"
                    }}</span>
                  </span>
                </td>

                <td class="px-6 py-4">
                  @if (log.data) {
                    <button
                      type="button"
                      (click)="openJsonModal(log.data, log)"
                      pTooltip="View Payload"
                      tooltipPosition="top"
                      [style.--btn-accent]="getTimelineColor(log.status)"
                      class="payload-btn flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 transition-all duration-200"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M10 3v4a1 1 0 0 1-1 1H5m4 10v-2m3 2v-6m3 6v-3m4-11v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1Z"
                        />
                      </svg>
                    </button>
                  }
                </td>

                <td
                  class="px-6 py-4 text-gray-600 dark:text-gray-300 min-w-[220px] max-w-md whitespace-normal break-words leading-relaxed"
                  [title]="log.remark"
                >
                  {{ log.remark }}
                </td>

                <td
                  class="px-6 py-4 text-gray-500 dark:text-gray-400 whitespace-nowrap"
                >
                  {{ log.created_on | appDate }}
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="6" class="px-6 py-16">
                  <div class="flex flex-col items-center justify-center">
                    <div
                      class="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3"
                    >
                      <i
                        class="pi pi-inbox text-2xl text-gray-400 dark:text-gray-500"
                      ></i>
                    </div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      No processing logs found for this attempt.
                    </p>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
}
<!-- Processed Data -->
@if (showJsonModal) {
  <div
    class="fixed inset-0 z-9999 flex items-center justify-center backdrop-blur-sm bg-black/40 dark:bg-black/60"
  >
    <div
      class="w-[95%] max-w-4xl max-h-[90vh] flex flex-col rounded-lg shadow-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
    >
      <!-- Header -->
      <div
        class="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          Processed Data
        </h2>

        <div class="flex items-center gap-3">
          @if (showAiExplainButton) {
            <button
              type="button"
              class="h-9 shrink-0 flex items-center gap-2 px-3 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              (click)="explainErrorWithAi()"
              [disabled]="isAiLoading"
              pTooltip="Explain this error with AI"
              tooltipPosition="top"
              appendTo="body"
            >
              <i class="pi pi-sparkles text-[0.85rem]"></i>
              <span>{{ isAiLoading ? "Explaining…" : "Ask AI" }}</span>
            </button>
          }
          <div class="relative mr-4">
            <p-iconfield iconPosition="left">
              <input
                class="w-50"
                type="text"
                pInputText
                placeholder="Search.."
                [(ngModel)]="pd_SearchText"
                (input)="
                  updateProcessDataMatchCount(); scrollToProcessDataMatch()
                "
              />
              @if (pd_SearchText) {
                <p-inputicon
                  class="pi pi-times"
                  (click)="onClearProcessDataSearch()"
                  style="right: 20%"
                />
              }
              <p-inputicon class="pi pi-search" />
            </p-iconfield>
          </div>
          @if (structuredRows) {
            <div
              class="shrink-0 flex items-center rounded-full bg-gray-200 dark:bg-gray-700 p-0.5 text-xs font-medium"
              role="group"
              aria-label="Payload view"
            >
              <button
                type="button"
                (click)="processedView = 'structured'"
                class="flex items-center gap-1.5 px-3 h-8 rounded-full transition-colors"
                [ngClass]="processedView === 'structured' ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'"
                pTooltip="Structured view"
                tooltipPosition="top"
                appendTo="body"
              >
                <i class="pi pi-list text-[0.8rem]"></i>
                <span>Structured</span>
              </button>
              <button
                type="button"
                (click)="processedView = 'raw'"
                class="flex items-center gap-1.5 px-3 h-8 rounded-full transition-colors"
                [ngClass]="processedView === 'raw' ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'"
                pTooltip="Raw data"
                tooltipPosition="top"
                appendTo="body"
              >
                <i class="pi pi-code text-[0.8rem]"></i>
                <span>Raw</span>
              </button>
            </div>
          }
          <!-- Download Button -->
          <button
            type="button"
            class="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            (click)="downloadProcessedFile()"
            pTooltip="Download"
            tooltipPosition="top"
            appendTo="body"
          >
            <i class="pi pi-download text-[0.85rem]"></i>
          </button>

          <!-- Close Button -->
          <button
            class="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white text-xl"
            (click)="closeJsonModal()"
          >
            ✕
          </button>
        </div>
      </div>

      @if (
        showAiExplainButton && (isAiLoading || aiExplanation || aiErrorMessage)
      ) {
        <div class="px-6 pt-4">
          <div
            class="rounded-md p-4 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/30 text-sm text-gray-800 dark:text-gray-200"
          >
            <div
              class="flex items-center gap-2 pb-2 text-indigo-700 dark:text-indigo-300 font-semibold"
            >
              <i class="pi pi-sparkles text-[0.85rem]"></i>
              <span>AI explanation</span>
              <span class="ml-auto flex items-center gap-1">
                @if (aiExplanation && !isAiLoading) {
                  <button
                    type="button"
                    class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors"
                    (click)="copyAiExplanation()"
                    [pTooltip]="aiCopied ? 'Copied' : 'Copy explanation'"
                    tooltipPosition="top"
                    appendTo="body"
                  >
                    <i
                      class="pi text-[0.8rem]"
                      [class.pi-check]="aiCopied"
                      [class.pi-copy]="!aiCopied"
                    ></i>
                  </button>
                }
                <button
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors"
                  (click)="closeAiExplanation()"
                  pTooltip="Dismiss"
                  tooltipPosition="top"
                  appendTo="body"
                >
                  <i class="pi pi-times text-[0.8rem]"></i>
                </button>
              </span>
            </div>
            @if (isAiLoading) {
              <div class="flex flex-col items-center justify-center py-4">
                <svg
                  class="animate-spin w-7 h-7 text-indigo-500 mb-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                  />
                </svg>
                <div class="text-gray-500 dark:text-gray-400">
                  Analyzing this error…
                </div>
              </div>
            } @else if (aiErrorMessage) {
              <div class="text-red-600 dark:text-red-400">
                {{ aiErrorMessage }}
              </div>
            } @else {
              <markdown class="md-preview" [data]="aiExplanation"></markdown>
            }
          </div>
        </div>
      }

      <!-- Payload Content -->
      <div class="p-6 flex-1 overflow-hidden flex flex-col">
        @if (structuredRows && processedView === 'structured') {
          @if (payloadTree?.length) {
            <div class="shrink-0 flex items-center justify-end gap-1 pb-2">
              <button
                type="button"
                (click)="expandAllPayload()"
                class="px-2.5 h-7 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Expand all
              </button>
              <button
                type="button"
                (click)="collapseAllPayload()"
                class="px-2.5 h-7 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Collapse all
              </button>
            </div>
          }
          <div
            class="overflow-auto max-h-[65vh] rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700"
          >
            <ng-container *ngTemplateOutlet="payloadNodes; context: { $implicit: payloadTree }"></ng-container>
            <ng-template #payloadNodes let-nodes>
              @for (node of nodes; track node.path) {
                <div class="px-4 py-2 text-sm">
                  <div class="flex items-start gap-2 min-w-0">
                    @if (node.children) {
                      <button
                        type="button"
                        (click)="togglePayloadNode(node)"
                        class="mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center rounded text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        [attr.aria-label]="isPayloadExpanded(node) ? 'Collapse' : 'Expand'"
                      >
                        <i
                          class="pi pi-chevron-right text-[10px] transition-transform duration-200"
                          [class.rotate-90]="isPayloadExpanded(node)"
                        ></i>
                      </button>
                    } @else {
                      <span class="mt-0.5 w-5 shrink-0"></span>
                    }
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span
                          class="font-medium text-gray-500 dark:text-gray-400 break-words"
                          [innerHTML]="prettyPayloadKey(node.key) | highlight: pd_SearchText"
                        ></span>
                        @if (node.type === 'object' || node.type === 'array') {
                          <span
                            class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 whitespace-nowrap"
                          >
                            {{ node.type === 'object' ? '{' + node.count + '}' : '[' + node.count + ']' }}
                          </span>
                        } @else {
                          <span
                            class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-gray-100 dark:bg-gray-700/60 text-gray-400 dark:text-gray-500 whitespace-nowrap"
                          >
                            {{ node.type }}
                          </span>
                        }
                        <span class="ml-auto flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            (click)="copyPayloadText(node.path, node.path)"
                            pTooltip="Copy path"
                            tooltipPosition="top"
                            appendTo="body"
                            class="w-6 h-6 flex items-center justify-center rounded text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            <i class="pi text-[0.7rem]" [class.pi-check]="copiedPayloadPath === node.path" [class.pi-hashtag]="copiedPayloadPath !== node.path"></i>
                          </button>
                        </span>
                      </div>
                      @if (!node.children) {
                        <div class="mt-0.5 min-w-0 break-all text-gray-800 dark:text-gray-200">
                          @if (node.type === 'null') {
                            <span class="text-gray-400 dark:text-gray-500">—</span>
                          } @else if (isUrlValue(node.value)) {
                            <a
                              href="{{ node.value }}"
                              target="_blank"
                              rel="noopener"
                              class="text-blue-600 dark:text-blue-400 hover:underline break-all"
                            >
                              {{ node.value }}
                            </a>
                          } @else {
                            <span [innerHTML]="payloadValueText(node) | highlight: pd_SearchText"></span>
                          }
                        </div>
                      } @else if (!isPayloadExpanded(node)) {
                        <div class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 truncate">
                          {{ node.type === 'object' ? '{…}' : '[…]' }} — {{ node.count }} {{ node.count === 1 ? 'item' : 'items' }}
                        </div>
                      }
                    </div>
                    @if (!node.children) {
                      <button
                        type="button"
                        (click)="copyPayloadText(payloadValueText(node), node.path + '#value')"
                        pTooltip="Copy value"
                        tooltipPosition="top"
                        appendTo="body"
                        class="mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center rounded text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        <i class="pi text-[0.7rem]" [class.pi-check]="copiedPayloadPath === node.path + '#value'" [class.pi-copy]="copiedPayloadPath !== node.path + '#value'"></i>
                      </button>
                    }
                  </div>
                  @if (node.children && isPayloadExpanded(node)) {
                    <div class="ml-5 mt-1 border-l border-gray-200 dark:border-gray-700 pl-2">
                      <ng-container *ngTemplateOutlet="payloadNodes; context: { $implicit: node.children }"></ng-container>
                    </div>
                  }
                </div>
              }
            </ng-template>
            @if (!payloadTree?.length) {
              <div
                class="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500"
              >
                No fields match the current search.
              </div>
            }
          </div>
        } @else {
          <div
            class="flex justify-between text-sm min-w-30 text-gray-400 pb-2"
            *ngIf="pd_MatchCount > 0"
          >
            Matches: {{ pd_MatchCount }}
          </div>
          <pre
            #p_datafileContainer
            class="text-sm rounded-md p-4 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 w-full max-w-full break-words overflow-auto max-h-[65vh]"
            [innerHTML]="selectedJsonData | highlight: pd_SearchText"
          ></pre>
        }
      </div>
    </div>
  </div>
}
<!-- Input File -->
@if (isInputFileModalVisible) {
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
  >
    <div
      class="w-[95%] max-w-4xl max-h-[80vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700"
      >
        <h2
          class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 min-w-0"
        >
          <span class="whitespace-nowrap">Input File :</span>

          <span
            class="block max-w-[200px] xl:max-w-[400px] truncate whitespace-nowrap overflow-hidden text-ellipsis"
            [pTooltip]="rowData.document_source_url.file_name"
            tooltipPosition="top"
          >
            {{ rowData.document_source_url.file_name }}
          </span>
        </h2>

        <div class="flex items-center gap-3">
          <div class="relative mr-4">
            <p-iconfield iconPosition="left">
              <input
                class="w-50"
                type="text"
                pInputText
                placeholder="Search.."
                [(ngModel)]="searchText"
                (input)="updateMatchCount(); scrollToFirstMatch()"
              />
              @if (searchText) {
                <p-inputicon
                  class="pi pi-times"
                  (click)="onClearInputSearch()"
                  style="right: 20%"
                />
              }
              <p-inputicon class="pi pi-search" />
            </p-iconfield>
          </div>
          <button
            type="button"
            class="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            (click)="downloadInputFile(rowData)"
            [disabled]="!inputFileContent"
            pTooltip="Download"
            tooltipPosition="top"
            appendTo="body"
          >
            <i class="pi pi-download text-[0.85rem]"></i>
          </button>

          <!-- Close Button -->
          <button
            class="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white text-xl"
            (click)="
              isInputFileModalVisible = false; searchText = ''; matchCount = 0
            "
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="max-h-[70vh] overflow-auto p-6">
        <div
          class="flex justify-between text-sm min-w-30 text-gray-400 pb-2"
          *ngIf="matchCount > 0"
        >
          Matches: {{ matchCount }}
        </div>
        @if (isFileLoading) {
          <div class="text-center text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        } @else {
          <pre
            #inputfileContainer
            class="text-sm p-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 overflow-auto"
            [innerHTML]="inputFileContent | highlight: searchText"
          ></pre>
        }
      </div>
    </div>
  </div>
}

<!-- Output File -->
@if (isOutputFileModalVisible) {
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
  >
    <div
      class="w-[95%] max-w-4xl max-h-[80vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700"
      >
        <h2
          class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 min-w-0"
        >
          <span class="whitespace-nowrap">Output File:</span>

          <span
            class="block max-w-[200px] xl:max-w-[400px] truncate whitespace-nowrap overflow-hidden text-ellipsis"
            [pTooltip]="selectedOutputFileFormatName"
            tooltipPosition="top"
          >
            {{ selectedOutputFileFormatName }}
          </span>
        </h2>

        <div class="flex items-center gap-3">
          <div class="relative mr-4">
            <p-iconfield iconPosition="left">
              <input
                class="w-50"
                type="text"
                pInputText
                placeholder="Search.."
                [(ngModel)]="outSearchText"
                (input)="updateOutputMatchCount(); scrollToOutputMatch()"
              />
              @if (outSearchText) {
                <p-inputicon
                  class="pi pi-times"
                  (click)="onClearOutputSearch()"
                  style="right: 20%"
                />
              }
              <p-inputicon class="pi pi-search" />
            </p-iconfield>
          </div>
          <!-- Download Button -->
          <button
            type="button"
            class="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            (click)="downloadOutputFile()"
            [disabled]="!outputFileContent"
            pTooltip="Download"
            tooltipPosition="top"
            appendTo="body"
          >
            <i class="pi pi-download text-[0.85rem]"></i>
          </button>

          <!-- Close Button -->
          <button
            class="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white text-xl"
            (click)="
              isOutputFileModalVisible = false;
              outSearchText = '';
              outMatchCount = 0
            "
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="max-h-[70vh] overflow-auto p-6">
        <div
          class="flex justify-between text-sm min-w-30 text-gray-400 pb-2"
          *ngIf="outMatchCount > 0"
        >
          Matches: {{ outMatchCount }}
        </div>
        @if (isOutputFileLoading) {
          <div class="text-center text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        } @else {
          <pre
            #outputfileContainer
            class="text-sm p-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 overflow-auto"
            [innerHTML]="outputFileContent | highlight: outSearchText"
          ></pre>
        }
      </div>
    </div>
  </div>
}
<!-- Acknowledgement File -->
@if (isAcknowledegeModalVisible) {
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
  >
    <div
      class="w-[95%] max-w-4xl max-h-[80vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          Acknowledgement File
        </h2>

        <div class="flex items-center gap-3">
          <div class="relative mr-4">
            <p-iconfield iconPosition="left">
              <input
                class="w-50"
                type="text"
                pInputText
                placeholder="Search.."
                [(ngModel)]="ackSearchText"
                (input)="updateAckMatchCount(); scrollToAckMatch()"
              />
              @if (ackSearchText) {
                <p-inputicon
                  class="pi pi-times"
                  (click)="onClearAckSearch()"
                  style="right: 20%"
                />
              }
              <p-inputicon class="pi pi-search" />
            </p-iconfield>
          </div>
          <!-- Download Button -->
          <button
            type="button"
            class="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            (click)="downloadAcknowledgeFile()"
            [disabled]="!acknowledegeFileConent"
            pTooltip="Download"
            tooltipPosition="top"
            appendTo="body"
          >
            <i class="pi pi-download text-[0.85rem]"></i>
          </button>

          <!-- Close Button -->
          <button
            class="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white text-xl"
            (click)="isAcknowledegeModalVisible = false"
          >
            ✕
          </button>
        </div>
      </div>
      <!-- Content -->
      <div class="max-h-[70vh] overflow-auto p-6">
        <div
          class="flex justify-between text-sm min-w-30 text-gray-400 pb-2"
          *ngIf="ackMatchCount > 0"
        >
          Matches: {{ ackMatchCount }}
        </div>
        @if (isAcknowledegeLoading) {
          <div class="text-center text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        } @else {
          <pre
            #ackContainer
            class="text-sm p-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 overflow-auto"
            [innerHTML]="acknowledegeFileConent | highlight: ackSearchText"
          ></pre>
        }
      </div>
    </div>
  </div>
}
<!-- Upload Modal -->
@if (showUploadModal) {
  <div
    class="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
  >
    <!-- Modal -->
    <div
      class="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h2 class="text-xl font-semibold text-gray-800 dark:text-white">
            Upload Files
            @if (selectedUploadFiles.length) {
              <span class="ml-2 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                {{ selectedUploadFiles.length }} of {{ maxFiles }}
              </span>
            }
          </h2>

          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Supported file types:
            <span class="font-medium">.csv, .json, .xml, .pff, .edi</span>
            <span class="text-gray-400"> · max 2MB each</span>
          </p>
        </div>

        <!-- Close -->
        <button
          type="button"
          (click)="closeUploadModal()"
          [disabled]="isUploading"
          class="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-500 disabled:opacity-50 dark:hover:bg-gray-800"
        >
          ✕
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-6">
        <div class="flex flex-col gap-4">
          <!-- Hidden File Input -->
          <input
            type="file"
            #fileInput
            class="hidden"
            accept=".csv,.json,.xml,.pff,.edi"
            multiple
            [disabled]="isUploading"
            (change)="onFileSelected($event)"
          />

          <!-- Upload Box -->
          <div
            (click)="!isUploading && fileInput.click()"
            (dragover)="onUploadDragOver($event)"
            (dragleave)="onUploadDragLeave($event)"
            (drop)="onUploadDrop($event)"
            class="cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition"
            [ngClass]="uploadDragOver
              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
              : 'border-gray-200 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/50 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:bg-indigo-900/20'"
          >
            <div
              class="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm dark:bg-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-indigo-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16"
                />
              </svg>
            </div>

            <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {{
                selectedUploadFiles.length ? "Add more files" : "Click to choose files"
              }}
            </p>

            <p class="mt-1 text-xs text-gray-400">
              Or drag and drop here · click a file below to preview
            </p>
          </div>

          @if (uploadValidationError) {
            <p class="rounded-xl bg-red-50 px-4 py-2.5 text-xs text-red-600 dark:bg-red-500/10 dark:text-red-400">
              {{ uploadValidationError }}
            </p>
          }

          @if (isUploading) {
            <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800/60">
              <div class="mb-1.5 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Uploading… {{ uploadCompletedCount }} of {{ selectedUploadFiles.length }}</span>
                <span class="font-medium">{{ uploadOverallProgress }}%</span>
              </div>
              <div class="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  class="h-full rounded-full bg-indigo-500 transition-all"
                  [style.width.%]="uploadOverallProgress"
                ></div>
              </div>
            </div>
          }

          <!-- Selected Files -->
          <div class="space-y-2">
            @for (file of selectedUploadFiles; track file.name + file.size + file.lastModified; let i = $index) {
              <div
                (click)="selectUploadPreview(i)"
                title="Click to preview"
                class="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 transition hover:border-indigo-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-600"
              >
                <!-- File Info -->
                <div class="flex min-w-0 items-center gap-3">
                  <!-- Ext badge -->
                  <span
                    class="flex h-10 w-12 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold uppercase tracking-wide"
                    [ngClass]="getUploadExtBadgeClass(getFileExtension(file))"
                  >
                    {{ getFileExtension(file).replace('.', '') || 'FILE' }}
                  </span>

                  <div class="min-w-0">
                    <p
                      class="truncate text-sm font-medium text-gray-800 dark:text-gray-100"
                      [title]="file.name"
                    >
                      {{ file.name }}
                    </p>

                    <p class="mt-0.5 text-xs text-gray-400">
                      {{ formatFileSize(file.size) }}
                      @if (uploadFileStatuses[i] === 'pending') {
                        <span> · Ready</span>
                      } @else if (uploadFileStatuses[i] === 'uploading') {
                        <span class="font-medium text-indigo-500"> · Uploading…</span>
                      } @else if (uploadFileStatuses[i] === 'done') {
                        <span class="font-medium text-emerald-600 dark:text-emerald-400"> · Uploaded</span>
                      } @else if (uploadFileStatuses[i] === 'failed') {
                        <span class="font-medium text-red-500"> · Failed — click to preview</span>
                      } @else {
                        <span> · Click to preview</span>
                      }
                    </p>
                    @if (uploadFileStatuses[i] === 'failed' && uploadFileErrors[i]) {
                      <p class="truncate text-xs text-red-500" [title]="uploadFileErrors[i]">
                        {{ uploadFileErrors[i] }}
                      </p>
                    }
                  </div>
                </div>

                <!-- Status / Remove -->
                <div class="flex shrink-0 items-center gap-2">
                  @if (uploadFileStatuses[i] === 'uploading') {
                    <i class="pi pi-spin pi-spinner text-indigo-500"></i>
                  } @else if (uploadFileStatuses[i] === 'done') {
                    <i class="pi pi-check-circle text-emerald-500"></i>
                  } @else if (uploadFileStatuses[i] === 'failed') {
                    <i class="pi pi-exclamation-circle text-red-500"></i>
                  } @else {
                    <i class="pi pi-eye text-gray-300"></i>
                  }
                  <button
                    type="button"
                    (click)="$event.stopPropagation(); removeSelectedFile(i)"
                    [disabled]="isUploading"
                    class="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-red-500 disabled:opacity-50 dark:hover:bg-gray-700"
                  >
                    ✕
                  </button>
                </div>
              </div>
            }
            @if (!selectedUploadFiles.length) {
              <p class="rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-center text-xs text-gray-400 dark:border-gray-700">
                No files selected yet.
              </p>
            }
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700">
        <p class="text-xs text-gray-500 dark:text-gray-400">
          @if (uploadSummary) {
            {{ uploadSummary }}
          } @else if (selectedUploadFiles.length) {
            {{ selectedUploadFiles.length }} file{{ selectedUploadFiles.length === 1 ? '' : 's' }} ready
          } @else {
            Choose files to begin.
          }
        </p>
        <div class="flex justify-end gap-3">
          <!-- Cancel / Done -->
          <button
            type="button"
            (click)="closeUploadModal()"
            [disabled]="isUploading"
            class="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {{ uploadSummary ? 'Done' : 'Cancel' }}
          </button>

          @if (hasFailedUploads && !isUploading) {
            <button
              type="button"
              (click)="retryFailedUploads()"
              class="rounded-xl border border-amber-400 px-5 py-2.5 text-sm font-medium text-amber-600 transition hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-500/10"
            >
              Retry failed
            </button>
          }

          <!-- Upload -->
          <button
            pButton
            [loading]="isUploading"
            (click)="onUploadClick()"
            [disabled]="!selectedUploadFiles.length || isUploading"
            class="rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-medium text-white shadow-md transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Upload{{ selectedUploadFiles.length > 1 ? ' ' + selectedUploadFiles.length + ' files' : '' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- File preview modal -->
  @if (previewIndex !== null && selectedUploadFiles[previewIndex]) {
    <div
      class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      (click)="closeUploadPreview()"
    >
      <div
        class="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900"
        (click)="$event.stopPropagation()"
      >
        <div class="flex items-center justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-gray-800">
          <div class="flex min-w-0 items-center gap-3">
            <span
              class="flex h-10 w-12 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold uppercase tracking-wide"
              [ngClass]="getUploadExtBadgeClass(getFileExtension(selectedUploadFiles[previewIndex]))"
            >
              {{ getFileExtension(selectedUploadFiles[previewIndex]).replace('.', '') || 'FILE' }}
            </span>
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-gray-800 dark:text-gray-100" [title]="selectedUploadFiles[previewIndex].name">
                {{ selectedUploadFiles[previewIndex].name }}
              </p>
              <p class="mt-0.5 text-xs text-gray-400">
                {{ formatFileSize(selectedUploadFiles[previewIndex].size) }} · {{ getFileExtension(selectedUploadFiles[previewIndex]).replace('.', '').toUpperCase() || 'FILE' }}
              </p>
            </div>
          </div>
          <button
            type="button"
            (click)="closeUploadPreview()"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
          >
            ✕
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-6 py-4 dark:bg-gray-800/40">
          @if (previewLoading) {
            <p class="py-8 text-center text-xs text-gray-400">Loading preview…</p>
          } @else if (previewError) {
            <p class="py-8 text-center text-xs text-red-500">{{ previewError }}</p>
          } @else {
            <pre class="whitespace-pre-wrap break-words font-mono text-xs leading-6 text-gray-700 dark:text-gray-200">{{ previewText || 'Empty file.' }}</pre>
            @if (previewTruncated) {
              <p class="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-600 dark:bg-amber-500/10 dark:text-amber-300">
                Preview truncated at 500KB — full file will still upload.
              </p>
            }
          }
        </div>
        <div class="flex justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-gray-800">
          <button
            type="button"
            (click)="closeUploadPreview()"
            class="rounded-xl border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  }
}
<!-- Sidebar Drawer -->
<div
  class="fixed top-0 right-0 h-full w-[380px] bg-white dark:bg-gray-900 shadow-2xl z-[9999] transform transition-transform duration-300 ease-in-out"
  [ngClass]="{
    'translate-x-0': isFilterOpen,
    'translate-x-full': !isFilterOpen,
  }"
>
  <div class="w-full bg-white dark:bg-gray-900 flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-2">
      <h2 class="text-lg font-semibold">Filters</h2>
      <button (click)="closeFilter()" class="text-gray-500 hover:text-gray-700">
        ✕
      </button>
    </div>

    <!-- Buttons -->
    <div class="px-6 py-4 border-b">
      <div class="flex gap-3">
        <button
          type="button"
          (click)="applyFilters()"
          [disabled]="isFilterLoading"
          class="w-full bg-indigo-500 text-white py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          APPLY
        </button>
        <button class="w-full border py-2 rounded-lg" (click)="resetFilters()">
          RESET
        </button>
      </div>
    </div>

    <!-- Scrollable Body -->
    <div class="flex-1 overflow-y-auto p-6 space-y-5 relative">
      <div class="flex flex-col gap-2">
        <label for="dtacknowledgementStatus" class="text-sm font-medium"> Acknowledgement Status </label>
        <p-select inputId="dtacknowledgementStatus"
          [options]="acknowledgementStatusOptions || []"
          [(ngModel)]="acknowledgementStatus"
          optionLabel="value"
          optionValue="value"
          placeholder="Select"
          class="w-full"
          [showClear]="true"
          [filter]="true"
        ></p-select>
        <label for="dtslaFilter" class="text-sm font-medium mt-2"> SLA Exceeded </label>
        <p-select inputId="dtslaFilter"
          [options]="slaFilterOptions || []"
          [(ngModel)]="slaFilter"
          optionLabel="value"
          optionValue="value"
          placeholder="Select"
          class="w-full"
          [showClear]="true"
          [filter]="true"
        ></p-select>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium"> Correlation </span>
        <label for="dtcorrelationKey" class="text-xs text-gray-500 dark:text-gray-400">
          Reference Key
        </label>
        <input id="dtcorrelationKey"
          pInputText
          type="text"
          [(ngModel)]="correlationKey"
          maxlength="200"
          placeholder="Enter reference key"
          class="w-full"
        />
        <label for="dtcorrelationValue" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Reference Value
        </label>
        <input id="dtcorrelationValue"
          pInputText
          type="text"
          [(ngModel)]="correlationValue"
          maxlength="200"
          placeholder="Enter reference value"
          class="w-full"
        />
      </div>

      @for (col of filterable_columns; track col.key) {
        <div class="flex flex-col gap-2">
          <label [attr.for]="'dt-filter-' + col.key" class="text-sm font-medium">
            {{ col.label }}
          </label>

          @if (col.filter_type === "input" || col.filter_type === "text") {
            <input [attr.id]="'dt-filter-' + col.key"
              pInputText
              type="text"
              [(ngModel)]="filterModel[col.key]"
              class="w-full"
            />
          } @else if (col.filter_type === "dropdown") {
            <p-select
              [options]="col.filter_options || []"
              [(ngModel)]="filterModel[col.key]"
              optionLabel="value"
              optionValue="id"
              placeholder="Select"
              class="w-full"
              [showClear]="true"
              [filter]="true"
              [loading]="isColumnLoading(col)"
            ></p-select>
          } @else if (col.filter_type === "multiselect") {
            <p-multiSelect
              [options]="col.filter_options || []"
              [(ngModel)]="filterModel[col.key]"
              [optionLabel]="'value'"
              [optionValue]="'id'"
              placeholder="Select"
              class="w-full text-sm"
              [filter]="true"
              [loading]="isColumnLoading(col)"
            ></p-multiSelect>
          } @else if (col.filter_type === "calendar") {
            <p-datepicker
              [(ngModel)]="filterModel[col.key]"
              selectionMode="range"
              dateFormat="yy-mm-dd"
              placeholder="Select Date Range"
              class="w-full"
              [showIcon]="true"
              [showButtonBar]="true"
              [maxDate]="today"
              [selectOtherMonths]="true"
            ></p-datepicker>
          }
        </div>
      }
    </div>
  </div>
</div>

<!-- Column Settings -->
<div
  *ngIf="showColumnSettings"
  class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 dark:bg-black/60 backdrop-blur-sm p-4"
  (click)="closeColumnSetting()"
>
  <div
    class="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    (click)="$event.stopPropagation()"
  >
    <!-- Modal Header -->
    <!-- HEADER -->
    <div
      class="flex justify-between items-center px-6 py-5 border-b border-slate-200/80 dark:border-slate-700 bg-gradient-to-r from-indigo-50 via-white to-violet-50 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30 flex items-center justify-center"
        >
          <i class="pi pi-sliders-h text-white text-lg"></i>
        </div>

        <div>
          <h3 class="text-lg font-bold text-slate-900 dark:text-white">
            Column Settings
          </h3>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            Customize your table experience
          </p>
        </div>
      </div>

      <button
        (click)="closeColumnSetting()"
        class="w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 transition-all duration-200"
      >
        <i class="pi pi-times text-lg"></i>
      </button>
    </div>

    <!-- Info Banner -->
    <div
      class="px-6 py-3 bg-gradient-to-r from-indigo-50 via-blue-50 to-violet-50 dark:from-indigo-950/30 dark:via-slate-900 dark:to-violet-950/30 border-b border-indigo-100 dark:border-slate-700"
    >
      <p
        class="text-xs text-indigo-700 dark:text-indigo-300 flex items-center gap-2"
      >
        <i class="pi pi-info-circle"></i>
        <span>Drag columns to reorder • Locked columns cannot be hidden</span>
      </p>
    </div>

    <!-- Draggable List Container -->
    <div class="p-4 space-y-6 max-h-[400px] overflow-y-auto">
      <!-- ========================================== -->
      <!-- SECTION 1: FROZEN (No drag, Lock icon)     -->
      <!-- ========================================== -->
      <div *ngIf="frozenColumns.length > 0">
        <p
          class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 px-1"
        >
          <i class="pi pi-lock mr-1"></i> Frozen Columns
        </p>
        <div class="space-y-2">
          <div
            *ngFor="let col of frozenColumns; trackBy: trackByColumn"
            class="flex items-center gap-3 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/80 opacity-75"
          >
            <div
              class="w-9 h-9 rounded-xl flex items-center justify-center opacity-40"
            >
              <i class="pi pi-lock text-slate-400 text-sm"></i>
            </div>

            <div class="flex-1 min-w-0">
              <span
                class="text-sm font-semibold truncate block text-slate-800 dark:text-slate-200"
                >{{ col.label }}</span
              >
            </div>

            <div
              class="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center"
            >
              <i
                class="pi pi-lock text-amber-500 dark:text-amber-400 text-sm"
              ></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- SECTION 2: NORMAL (Drag, Lock OR Eye)      -->
      <!-- ========================================== -->
      <div *ngIf="normalColumns.length > 0">
        <p
          class="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 px-1"
        >
          <i class="pi pi-list mr-1"></i> Visible Columns
        </p>

        <div
          cdkDropList
          [cdkDropListData]="normalColumns"
          (cdkDropListDropped)="drop($event)"
          class="space-y-2"
        >
          <div
            *ngFor="let col of normalColumns; trackBy: trackByColumn"
            cdkDrag
            class="flex items-center gap-3 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/80 transition-all duration-300 group"
            [ngClass]="{
              'opacity-50 bg-slate-100 dark:bg-slate-800':
                !col.is_selected && !isDraggableLocked(col),
            }"
          >
            <!-- Drag Handle -->
            <div
              cdkDragHandle
              class="p-2 rounded-xl cursor-grab active:cursor-grabbing hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
            >
              <i
                class="pi pi-bars text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400"
              ></i>
            </div>

            <!-- Column Label -->
            <div class="flex-1 min-w-0">
              <span
                class="text-sm font-semibold truncate block"
                [ngClass]="
                  col.is_selected || isDraggableLocked(col)
                    ? 'text-slate-800 dark:text-slate-200'
                    : 'text-slate-400 dark:text-slate-500'
                "
              >
                {{ col.label }}
              </span>
            </div>

            <!-- RIGHT SIDE: Conditionally show Lock OR Eye Toggle -->
            <ng-container
              *ngIf="isDraggableLocked(col); else eyeToggleTemplate"
            >
              <!-- 1. LOCK ICON (For Draggable Locked columns) -->
              <div
                class="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center"
              >
                <i
                  class="pi pi-lock text-amber-500 dark:text-amber-400 text-sm"
                ></i>
              </div>
            </ng-container>

            <ng-template #eyeToggleTemplate>
              <!-- 2. EYE TOGGLE (For Normal columns) -->
              <button
                type="button"
                (click)="toggleColumn(col)"
                class="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                [ngClass]="
                  col.is_visible
                    ? 'bg-emerald-50 dark:bg-emerald-500/10'
                    : 'bg-red-50 dark:bg-red-500/10'
                "
              >
                <i
                  class="pi text-base"
                  [ngClass]="
                    col.is_visible
                      ? 'pi-eye text-emerald-600 dark:text-emerald-400'
                      : 'pi-eye-slash text-red-600 dark:text-red-400'
                  "
                ></i>
              </button>
            </ng-template>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        *ngIf="!column_list?.length"
        class="text-center py-12 text-slate-400 dark:text-slate-500"
      >
        <div
          class="mx-auto w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4"
        >
          <i class="pi pi-table text-2xl"></i>
        </div>
        <p class="text-sm font-medium">No columns available</p>
      </div>
    </div>

    <!-- Modal Footer -->
    <!-- FOOTER -->
    <div
      class="flex items-center justify-between px-6 py-5 border-t border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800"
    >
      <!-- Reset -->
      <button
        (click)="resetColumns()"
        class="px-4 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:shadow-md transition-all duration-200 flex items-center gap-2"
      >
        <i class="pi pi-refresh text-xs"></i>
        Reset
      </button>

      <div class="flex items-center gap-3">
        <!-- Cancel -->
        <button
          (click)="closeColumnSetting()"
          class="px-5 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
        >
          <i class="pi pi-times text-xs"></i>
          Cancel
        </button>

        <!-- Save -->
        <button
          (click)="saveColumns()"
          class="px-6 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 hover:from-indigo-700 hover:via-violet-700 hover:to-purple-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2"
        >
          <i class="pi pi-check-circle"></i>
          Save Changes
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Error mesage for hyper links -->
<div
  *ngIf="showNavigationModal"
  class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
>
  <div
    class="w-full max-w-md overflow-hidden rounded-2xl border border-red-200 dark:border-red-500/20 bg-white dark:bg-gray-900 shadow-2xl animate-fadeIn"
  >
    <!-- Header -->
    <div
      class="flex items-center gap-3 bg-gradient-to-r from-red-500 to-rose-500 px-5 py-4 text-white"
    >
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20"
      >
        <i class="pi pi-exclamation-triangle text-lg"></i>
      </div>

      <div>
        <h3 class="font-semibold text-lg">
          {{ navigationModalTitle }}
        </h3>
        <p class="text-sm text-white/80">Unable to open requested item</p>
      </div>
    </div>

    <!-- Content -->
    <div class="px-5 py-5">
      <p class="text-sm leading-6 text-gray-600 dark:text-gray-300">
        {{ navigationModalMessage }}
      </p>

      <div
        class="mt-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10 px-4 py-3"
      >
        <div class="flex items-start gap-2">
          <i class="pi pi-info-circle text-amber-500 mt-0.5"></i>

          <span class="text-xs text-amber-700 dark:text-amber-300">
            This record may have been deleted or is no longer available.
          </span>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex justify-end border-t border-gray-200 dark:border-gray-700 px-5 py-4"
    >
      <button
        type="button"
        (click)="showNavigationModal = false"
        class="rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-medium text-white shadow-md transition hover:scale-105"
      >
        Got it
      </button>
    </div>
  </div>
</div>
<!-- FLOATING BULK ACTION BAR -->
<!-- Bulk Action Menu -->
<p-menu
  #bulkActionMenu
  [model]="bulkActionMenuItems"
  [popup]="true"
  appendTo="body"
></p-menu>

<!-- Bulk Actions Bar - show only for 2+ selections -->
<!-- Bulk Actions Bar - show only for 2+ selections -->
<div *ngIf="selectedCount >= 2" class="bulk-bar">
  <!-- Selection count badge -->
  <div class="bulk-count">
    <i class="pi pi-check-square"></i>
    <span>{{ selectedCount }} selected</span>
  </div>

  <div class="bulk-divider"></div>

  <!-- PRIMARY: Send Email button -->
  <button
    *ngIf="canBulkSendMail()"
    type="button"
    (click)="onBulkSendEmail()"
    [pTooltip]="getActionLabel(Settings.StatusEvent.SEND_EMAIL)"
    tooltipPosition="top"
    class="bulk-btn bulk-btn--indigo"
  >
    <i [class]="'pi ' + getActionIcon(Settings.StatusEvent.SEND_EMAIL)"></i>
  </button>

  <!-- SECONDARY: All available action buttons -->
  <ng-container *ngIf="getBulkSecondaryActions() as secondaryActions">
    <div *ngIf="secondaryActions.length > 0" class="bulk-divider"></div>

    <button
      *ngFor="let action of secondaryActions"
      type="button"
      [pTooltip]="getActionLabel(action)"
      tooltipPosition="top"
      class="bulk-btn"
      [ngClass]="{
        'bulk-btn--blue': action === Settings.StatusEvent.RESENT,
        'bulk-btn--purple': action === Settings.StatusEvent.REPROCESS,
        'bulk-btn--green': action === Settings.StatusEvent.RELEASE,
        'bulk-btn--red': action === Settings.StatusEvent.DISCARD,
        'bulk-btn--amber': action === Settings.StatusEvent.MARK_COMPLETE,
      }"
      (click)="onBulkActionSelect(action)"
    >
      <i [class]="'pi ' + getActionIcon(action)"></i>
    </button>
  </ng-container>

  <!-- No actions configured -->
  <span
    *ngIf="!canBulkSendMail() && !hasBulkSecondaryActions()"
    class="bulk-empty"
  >
    No actions available
  </span>

  <div class="bulk-divider"></div>

  <!-- Clear Selection button -->
  <button
    type="button"
    (click)="clearSelection()"
    pTooltip="Clear Selection"
    tooltipPosition="top"
    class="bulk-btn bulk-btn--clear"
  >
    <i class="pi pi-times"></i>
  </button>
</div>
<!-- SEND EMAIL MODAL Row wise-->
<div
  *ngIf="showSendEmailModal"
  class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
  (click)="closeSendEmailModal()"
>
  <!-- Modal -->
  <div
    class="bg-white dark:bg-gray-900 w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
    (click)="$event.stopPropagation()"
  >
    <!-- ================= Header ================= -->
    <!-- ================= Header ================= -->
    <div class="flex items-start justify-between px-8 py-2">
      <div class="flex items-center gap-4">
        <!-- Icon -->
        <div
          class="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg flex-shrink-0"
        >
          <i class="pi pi-envelope text-white text-xl"></i>
        </div>

        <h2
          class="text-xl font-bold text-gray-800 dark:text-white flex-shrink-0"
        >
          Send Email
        </h2>

        <!-- Row info capsule -->
        <!-- Row info capsule -->
        <!-- Row info capsule -->
        <div
          *ngIf="selectedEmailRowData"
          class="flex items-center flex-wrap gap-x-2 gap-y-1 px-3 py-1 rounded-full bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 text-[10px] text-gray-600 dark:text-gray-300"
        >
          <span *ngIf="selectedEmailRowData.wfid">
            <span class="font-semibold text-violet-600 dark:text-violet-400"
              >WF ID:</span
            >
            {{ selectedEmailRowData.wfid }}
          </span>

          <span
            *ngIf="selectedEmailRowData.sender_profile"
            class="text-violet-300"
            >|</span
          >
          <span *ngIf="selectedEmailRowData.sender_profile">
            <span class="font-semibold text-violet-600 dark:text-violet-400"
              >Sender:</span
            >
            {{ selectedEmailRowData.sender_profile }}
          </span>

          <span
            *ngIf="selectedEmailRowData.receiver_profile"
            class="text-violet-300"
            >|</span
          >
          <span *ngIf="selectedEmailRowData.receiver_profile">
            <span class="font-semibold text-violet-600 dark:text-violet-400"
              >Receiver:</span
            >
            {{ selectedEmailRowData.receiver_profile }}
          </span>

          <span
            *ngIf="selectedEmailRowData.in_file_type"
            class="text-violet-300"
            >|</span
          >
          <span *ngIf="selectedEmailRowData.in_file_type">
            <span class="font-semibold text-violet-600 dark:text-violet-400"
              >In:</span
            >
            {{ selectedEmailRowData.in_file_type }}
          </span>

          <span
            *ngIf="selectedEmailRowData.out_file_type"
            class="text-violet-300"
            >|</span
          >
          <span *ngIf="selectedEmailRowData.out_file_type">
            <span class="font-semibold text-violet-600 dark:text-violet-400"
              >Out:</span
            >
            {{ selectedEmailRowData.out_file_type }}
          </span>
        </div>
      </div>

      <button
        (click)="closeSendEmailModal()"
        class="text-3xl text-gray-400 hover:text-red-500 transition flex-shrink-0"
      >
        ×
      </button>
    </div>

    <!-- Divider -->
    <div class="border-t border-gray-200 dark:border-gray-700"></div>

    <!-- ================= Body ================= -->
    <form
      [formGroup]="sendEmailForm"
      class="px-8 py-6 max-h-[65vh] overflow-y-auto space-y-6"
    >
      <!-- Row 1: Template toggle (left) + template dropdown (right, when on) -->
      <div class="grid grid-cols-2 gap-4">
        <!-- Left column: toggle -->
        <div class="flex items-center gap-3 mt-7">
          <label for="dtuse-template" class="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Show Template Names
          </label>
          <p-toggleswitch inputId="dtuse-template"
            formControlName="use_template"
            (onChange)="onToggleTemplate()"
            class="ml-10"
          ></p-toggleswitch>
        </div>

        <!-- Right column: template dropdown (only when toggle is on) -->
        <div *ngIf="sendEmailForm.get('use_template')?.value">
          <label for="dttemplate-id"
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            Template
          </label>
          <p-select inputId="dttemplate-id"
            [options]="emailTemplates"
            formControlName="template_id"
            optionLabel="template_name"
            optionValue="id"
            placeholder="Select a template"
            appendTo="body"
            [loading]="loadingTemplates"
            class="w-full"
            (onChange)="onTemplateSelect()"
          ></p-select>
        </div>
      </div>

      <!-- Row 2: To (left) + CC (right) -->
      <div class="grid grid-cols-2 gap-4">
        <!-- To -->
        <div>
          <label for="dtto"
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            To
            <span class="text-red-500">*</span>
          </label>
          <input id="dtto"
            formControlName="to"
            type="text"
            placeholder="Enter recipient email"
            class="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100 dark:focus:ring-violet-900 outline-none transition"
            [ngClass]="{
              'border-red-500':
                sendEmailForm.get('to')?.invalid &&
                sendEmailForm.get('to')?.touched,
            }"
          />
          <small
            *ngIf="
              sendEmailForm.get('to')?.invalid &&
              sendEmailForm.get('to')?.touched
            "
            class="text-red-500 text-xs mt-2 block"
          >
            Recipient email is required.
          </small>
        </div>

        <!-- CC -->
        <div>
          <label for="dtcc"
            class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            CC
            <span class="text-red-500">*</span>
          </label>
          <input id="dtcc"
            formControlName="cc"
            type="text"
            placeholder="Enter CC email"
            class="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100 dark:focus:ring-violet-900 outline-none transition"
            [ngClass]="{
              'border-red-500':
                sendEmailForm.get('cc')?.invalid &&
                sendEmailForm.get('cc')?.touched,
            }"
          />
          <small
            *ngIf="
              sendEmailForm.get('cc')?.invalid &&
              sendEmailForm.get('cc')?.touched
            "
            class="text-red-500 text-xs mt-2 block"
          >
            CC email is required.
          </small>
        </div>
      </div>

      <!-- Subject (with attachment icon beside the input) -->
      <div>
        <label for="dtsubject"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
        >
          Subject
          <span class="text-red-500">*</span>
        </label>

        <div class="flex items-center gap-3">
          <input id="dtsubject"
            formControlName="subject"
            type="text"
            placeholder="Enter subject"
            class="flex-1 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-100 dark:focus:ring-violet-900 outline-none transition"
            [ngClass]="{
              'border-red-500':
                sendEmailForm.get('subject')?.invalid &&
                sendEmailForm.get('subject')?.touched,
            }"
          />

          <!-- Attachment icon in a circle, beside the input -->
          <label
            *ngIf="selectedFiles.length < maxFiles"
            class="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 cursor-pointer text-gray-500 hover:text-violet-600 hover:border-violet-500 transition"
            title="Upload file (max {{ maxFiles }})"
          >
            <i class="pi pi-paperclip text-sm"></i>
            <input
              type="file"
              multiple
              class="hidden"
              (change)="onFilesSelected($event)"
            />
          </label>
        </div>

        <small
          *ngIf="
            sendEmailForm.get('subject')?.invalid &&
            sendEmailForm.get('subject')?.touched
          "
          class="text-red-500 text-xs mt-2 block"
        >
          Subject is required.
        </small>
      </div>

      <!-- File error -->
      <small *ngIf="fileError" class="text-red-500 text-xs mb-2 block">
        {{ fileError }}
      </small>

      <!-- Attached files side by side (before message) -->
      <div *ngIf="selectedFiles.length" class="flex flex-wrap gap-2 mb-3">
        <div
          *ngFor="let file of selectedFiles; let i = index"
          class="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 max-w-[180px]"
        >
          <i class="pi pi-file text-violet-500 text-xs flex-shrink-0"></i>
          <span
            class="text-xs text-gray-700 dark:text-gray-200 truncate"
            [pTooltip]="file.name"
            tooltipPosition="top"
          >
            {{ file.name }}
          </span>
          <button
            type="button"
            (click)="removeFile(i)"
            class="text-gray-400 hover:text-red-500 transition flex-shrink-0"
          >
            <i class="pi pi-times text-xs"></i>
          </button>
        </div>
      </div>

      <!-- Message -->
      <div>
        <label
          for="dt-email-body"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
        >
          Message
          <span class="text-red-500">*</span>
        </label>
        <!-- Toolbar -->
        <div
          class="flex items-center gap-5 px-4 py-3 rounded-t-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
        >
          <i class="pi pi-bold cursor-pointer hover:text-violet-600"></i>
          <i class="pi pi-underline cursor-pointer hover:text-violet-600"></i>
          <i class="pi pi-align-left cursor-pointer hover:text-violet-600"></i>
          <i class="pi pi-list cursor-pointer hover:text-violet-600"></i>
          <i
            class="pi pi-sort-numeric-down cursor-pointer hover:text-violet-600"
          ></i>
          <i class="pi pi-pencil cursor-pointer hover:text-violet-600"></i>
        </div>

        <textarea
          id="dt-email-body"
          rows="8"
          formControlName="body"
          placeholder="Type your message..."
          class="w-full rounded-b-xl border border-t-0 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm resize-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 dark:focus:ring-violet-900 outline-none transition"
          [ngClass]="{
            'border-red-500':
              sendEmailForm.get('body')?.invalid &&
              sendEmailForm.get('body')?.touched,
          }"
        ></textarea>
        <small
          *ngIf="
            sendEmailForm.get('body')?.invalid &&
            sendEmailForm.get('body')?.touched
          "
          class="text-red-500 text-xs mt-2 block"
        >
          Message is required.
        </small>
      </div>
    </form>

    <!-- Divider -->
    <div class="border-t border-gray-200 dark:border-gray-700"></div>

    <!-- ================= Footer ================= -->
    <div
      class="flex justify-between items-center px-5 py-3 bg-gray-50 dark:bg-gray-900"
    >
      <!-- Left -->
      <button
        type="button"
        (click)="resetEmailForm()"
        class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
      >
        <i class="pi pi-refresh mr-1 text-sm"></i>
        Reset
      </button>

      <!-- Right -->
      <div class="flex gap-2">
        <button
          type="button"
          (click)="closeSendEmailModal()"
          class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
        >
          <i class="pi pi-times mr-1 text-sm"></i>
          Cancel
        </button>

        <button
          pButton
          [loading]="isSendingEmail"
          (click)="submitSendEmail()"
          [disabled]="sendEmailForm.invalid"
          class="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-medium shadow hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i class="pi pi-send mr-1 text-sm"></i>
          Send Email
        </button>
      </div>
    </div>
  </div>
</div>
<!-- REMARKS MODAL -->
<div
  *ngIf="showRemarkModal"
  class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
  (click)="closeRemarkModal()"
>
  <div
    class="bg-white dark:bg-gray-900 w-full max-w-md rounded-3xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4">
      <h2 class="text-lg font-bold text-gray-800 dark:text-white">
        {{ remarkActionLabel }}
      </h2>
      <button
        (click)="closeRemarkModal()"
        class="text-2xl text-gray-400 hover:text-red-500 transition"
      >
        ×
      </button>
    </div>

    <div class="border-t border-gray-200 dark:border-gray-700"></div>

    <!-- Body -->
    <div class="px-6 py-5">
      <label for="dtremarkText"
        class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
      >
        Remark
        <span *ngIf="isRemarkRequired" class="text-red-500">*</span>
        <span *ngIf="!isRemarkRequired" class="text-gray-400 font-normal"
          >(optional)</span
        >
      </label>

      <textarea id="dtremarkText"
        rows="4"
        [(ngModel)]="remarkText"
        [placeholder]="
          isRemarkRequired ? 'Remark is required' : 'Add a note (optional)'
        "
        class="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm resize-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 dark:focus:ring-violet-900 outline-none transition"
        [ngClass]="{ 'border-red-500': isRemarkRequired && !remarkText.trim() }"
      ></textarea>

      <small
        *ngIf="isRemarkRequired && !remarkText.trim()"
        class="text-red-500 text-xs mt-2 block"
      >
        Remark is required for this action.
      </small>
    </div>

    <div class="border-t border-gray-200 dark:border-gray-700"></div>

    <!-- Footer -->
    <div class="flex justify-end gap-2 px-6 py-4 bg-gray-50 dark:bg-gray-900">
      <button
        type="button"
        (click)="closeRemarkModal()"
        class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
      >
        Cancel
      </button>

      <button
        pButton
        [loading]="isProceeding"
        (click)="confirmRemarkAction()"
        [disabled]="isProceedDisabled"
        class="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-medium shadow hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Proceed
      </button>
    </div>
  </div>
</div>
@if (loading) {
  <div
    class="dash-loader-fade fixed inset-0 z-50 flex items-center justify-center bg-white/60 dark:bg-slate-950/60 backdrop-blur-sm"
  >
    <div class="dash-loader-fade flex flex-col items-center gap-4">
      <div class="relative">
        <div class="dash-loader-glow"></div>
        <div class="dash-loader-ring"></div>
      </div>
      <span class="dash-loader-text text-sm font-semibold tracking-wide">
        Loading Document Tracker…
      </span>
      <div class="dash-loader-bar"></div>
    </div>
  </div>
}

<!-- Auto-refresh settings -->
<p-dialog
  header="Auto-refresh"
  [(visible)]="showAutoRefreshModal"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '380px' }"
>
  <p class="text-sm text-gray-500 dark:text-gray-400 m-0">
    Automatically refresh the list while this page is open.
  </p>
  <div class="flex flex-col gap-1.5 mt-3">
    @for (opt of autoRefreshOptions; track opt.value) {
      <button
        type="button"
        (click)="onAutoRefreshChange(opt.value)"
        [ngClass]="
          autoRefreshInterval === opt.value
            ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-900/30'
            : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 dark:border-gray-700 dark:hover:border-indigo-700 dark:hover:bg-indigo-900/10'
        "
        class="flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-colors"
      >
        <span
          class="flex items-center justify-center w-4 h-4 rounded-full border-2 transition-colors"
          [ngClass]="
            autoRefreshInterval === opt.value
              ? 'border-indigo-600'
              : 'border-gray-300 dark:border-gray-600'
          "
        >
          @if (autoRefreshInterval === opt.value) {
            <span class="w-2 h-2 rounded-full bg-indigo-600"></span>
          }
        </span>
        <span class="text-sm font-medium text-gray-700 dark:text-gray-200">{{
          opt.label
        }}</span>
        @if (autoRefreshInterval === opt.value) {
          <i class="pi pi-check ml-auto text-sm text-indigo-600"></i>
        }
      </button>
    }
  </div>
  @if (autoRefreshInterval > 0 && refreshInSeconds !== null) {
    <div
      class="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-800"
    >
      <i class="pi pi-clock text-sm text-indigo-500"></i>
      <span
        class="text-sm font-medium text-indigo-700 dark:text-indigo-300 tabular-nums"
      >
        Refreshing in {{ refreshInSeconds }}s…
      </span>
    </div>
  }
  <ng-template pTemplate="footer">
    <button
      type="button"
      (click)="showAutoRefreshModal = false"
      class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-all"
    >
      Done
    </button>
  </ng-template>
</p-dialog>
